<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" @click.self="handleClose">
    <div class="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto" @click.stop>
      <!-- 顶部 -->
      <div class="sticky top-0 bg-white border-b border-gray-200 p-4 md:p-6 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h2 class="text-lg md:text-xl font-bold text-gray-800">客服验证</h2>
        </div>
        <button @click="handleClose" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
          <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="p-4 md:p-6">
        <div v-if="verifyStatus === 'idle' || verifyStatus === 'verifying'">
          <!-- 验证方式选择 -->
          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-3">选择验证方式</label>
            <div class="grid grid-cols-2 gap-3">
              <button @click="setVerifyType('qq')" :class="['p-4 rounded-xl border-2 transition-all', verifyType === 'qq' ? 'border-pink-400 bg-gradient-to-br from-pink-50 to-purple-50' : 'border-gray-200 hover:border-pink-300']">
                <div class="font-medium text-gray-800 text-sm md:text-base">QQ号码</div>
              </button>
              <button @click="setVerifyType('phone')" :class="['p-4 rounded-xl border-2 transition-all', verifyType === 'phone' ? 'border-pink-400 bg-gradient-to-br from-pink-50 to-purple-50' : 'border-gray-200 hover:border-pink-300']">
                <div class="font-medium text-gray-800 text-sm md:text-base">手机号码</div>
              </button>
            </div>
          </div>

          <!-- 输入框 -->
          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              {{ verifyType === 'qq' ? '客服QQ号码' : '客服手机号码' }}
            </label>
            <input :type="verifyType === 'qq' ? 'text' : 'tel'" v-model="inputValue" :placeholder="verifyType === 'qq' ? '请输入客服QQ号码' : '请输入客服手机号码'" :disabled="verifyStatus === 'verifying'" class="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-pink-400 text-sm md:text-base" />
          </div>

          <!-- 提示信息 -->
          <div class="mb-6 p-4 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
            <p class="text-xs text-gray-700 leading-relaxed">
              <span class="font-medium text-orange-600">安全提示：</span>
              请确保您联系的是官方客服。如果对方要求您提供密码、验证码等敏感信息，请立即停止交流并联系平台举报。
            </p>
          </div>

          <!-- 验证示例 -->
          <div class="mb-6">
            <div class="text-xs text-gray-500 mb-2">官方客服示例：</div>
            <div class="space-y-2 text-xs">
              <div class="flex items-center gap-2 text-gray-600">
                <div class="w-1.5 h-1.5 rounded-full bg-pink-400"></div>
                <span>QQ：8888888 / 9999999 / 7777777</span>
              </div>
              <div class="flex items-center gap-2 text-gray-600">
                <div class="w-1.5 h-1.5 rounded-full bg-pink-400"></div>
                <span>电话：138-0013-8000 / 138-0013-8001</span>
              </div>
            </div>
          </div>

          <!-- 按钮 -->
          <button @click="handleVerify" :disabled="!inputValue.trim() || verifyStatus === 'verifying'" class="w-full py-3 md:py-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm md:text-base">
            <svg v-if="verifyStatus === 'verifying'" class="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>{{ verifyStatus === 'verifying' ? '验证中...' : '开始验证' }}</span>
          </button>
        </div>

        <div v-else-if="verifyStatus === 'success'" class="text-center py-8">
          <div class="w-20 h-20 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 class="text-2xl font-bold text-gray-800 mb-3">验证成功</h3>
          <p class="text-gray-600 mb-6">这是甜甜游戏交易网的官方客服</p>
          <div class="p-4 bg-gradient-to-br from-green-50 to-teal-50 rounded-xl mb-6 border border-green-200">
            <div class="text-sm text-gray-700 mb-2">已验证的客服信息：</div>
            <div class="font-medium text-green-600">
              {{ verifyType === 'qq' ? `QQ：${inputValue}` : `手机：${inputValue}` }}
            </div>
          </div>
          <div class="space-y-3">
            <button @click="handleReset" class="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full font-medium hover:shadow-lg transition-all">
              继续验证
            </button>
            <button @click="handleClose" class="w-full py-3 border-2 border-gray-200 text-gray-600 rounded-full font-medium hover:border-pink-400 hover:text-pink-600 transition-all">
              关闭
            </button>
          </div>
        </div>

        <div v-else class="text-center py-8">
          <div class="w-20 h-20 bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 class="text-2xl font-bold text-gray-800 mb-3">验证失败</h3>
          <p class="text-gray-600 mb-6">
            该{{ verifyType === 'qq' ? 'QQ号码' : '手机号码' }}不是官方客服
          </p>
          <div class="p-4 bg-gradient-to-br from-red-50 to-pink-50 rounded-xl mb-6 border border-red-200">
            <p class="text-sm text-red-600 leading-relaxed">
              <span class="font-medium">警告：</span>
              请勿与非官方客服进行交易！如对方声称是客服，请立即停止交流并联系官方举报。
            </p>
          </div>
          <div class="space-y-3">
            <button @click="handleReset" class="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full font-medium hover:shadow-lg transition-all">
              重新验证
            </button>
            <button @click="handleClose" class="w-full py-3 border-2 border-gray-200 text-gray-600 rounded-full font-medium hover:border-pink-400 hover:text-pink-600 transition-all">
              关闭
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 模拟的客服数据库
const validCustomerServices = [
  { qq: '8888888', phone: '13800138000' },
  { qq: '9999999', phone: '13800138001' },
  { qq: '7777777', phone: '13800138002' }
]

export default {
  name: 'CustomerServiceVerifyDialog',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      verifyType: 'qq',
      inputValue: '',
      verifyStatus: 'idle' // idle, verifying, success, failed
    }
  },
  methods: {
    setVerifyType(type) {
      this.verifyType = type
      this.inputValue = ''
    },
    handleVerify() {
      if (!this.inputValue.trim()) {
        return
      }

      this.verifyStatus = 'verifying'

      // 模拟验证过程
      setTimeout(() => {
        const isValid = validCustomerServices.some((cs) =>
          this.verifyType === 'qq' ? cs.qq === this.inputValue : cs.phone === this.inputValue
        )

        this.verifyStatus = isValid ? 'success' : 'failed'
      }, 1500)
    },
    handleClose() {
      this.inputValue = ''
      this.verifyStatus = 'idle'
      this.$emit('close')
    },
    handleReset() {
      this.inputValue = ''
      this.verifyStatus = 'idle'
    }
  }
}
</script>
